import { DEFAULT_CURRENCY } from '@constants/global';

export const numberFormatCache = new Map<string, Intl.NumberFormat>();

export interface FormatOptions {
  number: number;
  currency?: string;
  shortened?: boolean;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  removeSpaces?: boolean;
  showPlusPrefix?: boolean;
  showNegativePrefix?: boolean;
}

// Currencies that use Anglo number format (1,234.56) - NON EU
const ANGLO_CURRENCIES = new Set(['GBP', 'USD', 'AUD', 'CAD', 'NZD', 'JPY', 'ZAR']);

// Currencies that display symbol at the start
const SYMBOL_AT_START = new Set(['GBP', 'USD', 'AUD', 'CAD', 'NZD', 'CHF', 'TRY', 'BRL', 'ZAR', 'JPY']);

const getLocaleForCurrency = (currency: string): string => (ANGLO_CURRENCIES.has(currency) ? 'en-US' : 'de-DE');

const createCacheKey = (
  locale: string,
  currency: string,
  minFractionDigits: number,
  maxFractionDigits: number,
  shortened: boolean
): string => {
  return `${locale}-${currency}-${minFractionDigits}-${maxFractionDigits}-${shortened}`;
};

export const formatCurrency = (options: FormatOptions): string => {
  const currency = options.currency ?? DEFAULT_CURRENCY;
  const locale = getLocaleForCurrency(currency);

  const minFractionDigits = options.minimumFractionDigits === 0 ? 0 : options.minimumFractionDigits ?? 2;
  const maxFractionDigits =
    options.maximumFractionDigits === undefined
      ? Math.max(2, minFractionDigits)
      : Math.max(options.maximumFractionDigits, minFractionDigits);

  const cacheKey = createCacheKey(locale, currency, minFractionDigits, maxFractionDigits, options.shortened ?? false);

  let formatter = numberFormatCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(cacheKey, formatter);
  }

  // For currencies that need symbol at start but locale puts it at end, reposition
  const symbolAtEnd = !SYMBOL_AT_START.has(currency);
  const localeWouldPutSymbolAtEnd = !ANGLO_CURRENCIES.has(currency); // de-DE puts symbol at end

  let result: string;

  if (!symbolAtEnd && localeWouldPutSymbolAtEnd) {
    // Currency needs symbol at start, but de-DE locale puts it at end
    // Use formatToParts to reposition
    const parts = formatter.formatToParts(Math.abs(options.number));
    const symbol = parts.find((p) => p.type === 'currency')?.value ?? currency;
    const numberPart = parts
      .filter((p) => p.type !== 'currency' && p.type !== 'literal')
      .map((p) => p.value)
      .join('');

    result = `${symbol} ${numberPart}`;

    if (options.number < 0) {
      result = `-${result}`;
    }
  } else {
    // Use native Intl formatting
    result = formatter.format(options.number);
  }

  // Remove spaces between number and currency symbol (default: true)
  if (options.removeSpaces !== false) {
    // Define currency symbols that can appear at the end or beginning
    const endSymbols = ['€', '$', '£', '¥', '₹', '₽', '₩', 'zł', 'kr', 'Kč', 'Ft'];
    const startSymbols = ['€', '$', '£', '¥', '₹', '₽', '₩', '₺', 'R$', 'R', 'CHF'];

    // Remove whitespace before currency symbols at the end
    // Using a simpler, non-backtracking approach
    for (const symbol of endSymbols) {
      // Escape special regex characters ($ and .)
      const escapedSymbol = symbol.replaceAll('.', String.raw`\.`).replaceAll('$', String.raw`\$`);
      // Match whitespace followed by symbol at end, replace with just symbol
      const regex = new RegExp(String.raw`[\s\u00A0\u2000-\u200A\u202F\u205F]+(${escapedSymbol})$`);
      result = result.replace(regex, '$1');
    }

    // Remove whitespace after currency symbols at the beginning
    // Handle both positive and negative numbers (with - prefix)
    for (const symbol of startSymbols) {
      // Escape special regex characters ($ and .)
      const escapedSymbol = symbol.replaceAll('.', String.raw`\.`).replaceAll('$', String.raw`\$`);
      // Match optional minus, symbol, then whitespace, replace with minus + symbol
      const regex = new RegExp(String.raw`^(-?)(${escapedSymbol})[\s\u00A0\u2000-\u200A\u202F\u205F]+`);
      result = result.replace(regex, '$1$2');
    }
  }

  // Normalize non-breaking spaces to regular spaces
  result = result.replaceAll(/[\u00A0\u202F]/g, ' ');

  // Add plus prefix for positive numbers if requested
  if (options.showPlusPrefix && options.number > 0) {
    result = `+${result}`;
  }

  if (options.showNegativePrefix && options.number >= 0) {
    result = `-${result}`;
  }

  return result;
};

/**
 * Formats a plain number (no currency). Always Anglo format: 1,234
 */
export const formatValue = (options: FormatOptions): string => {
  const minFractionDigits = options.minimumFractionDigits ?? 0;
  const maxFractionDigits =
    options.maximumFractionDigits === undefined
      ? Math.max(0, minFractionDigits)
      : Math.max(options.maximumFractionDigits, minFractionDigits);

  const key = `decimal-${minFractionDigits}-${maxFractionDigits}-${options.shortened ?? false}`;

  let formatter = numberFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'decimal',
      useGrouping: true,
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(key, formatter);
  }

  return formatter.format(options.number);
};
